import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Map, ArrowUpRight, ListChecks, Landmark, Settings2, AlertCircle, Download } from "lucide-react";
import { downloadBlob, parseBlobError } from "@/lib/utils";
import {
  getProyeccionDrivers,
  getProyeccionOperaciones,
  getProyeccionConfig,
  exportarDriversEmpresa,
} from "@/lib/api";

// Mapa de hipótesis (queja 1b de Pau, 30-09-2026): "si Héctor aplica un
// driver yo no lo sé, tengo que ir entrando y mirando". UNA pestaña que lista
// TODO lo que está tocado en la proyección — drivers por línea (con fecha),
// operaciones financieras/CAPEX/eventos y parámetros del modelo — con un
// enlace a la pestaña donde se edita cada cosa. Solo lectura: aquí no se
// cambia nada, se VE todo.

const SECCION_LABEL = {
  ingresos: "Ingresos",
  otros_ingresos: "Otros ingresos",
  gastos: "Gastos",
};

const NIVEL_LABEL = { "1D": "1 dígito", "3D": "3 dígitos", MAX: "Máx. dígitos" };

const TIPO_OPERACION_LABEL = {
  prestamo: "Préstamo nuevo",
  prestamo_existente: "Préstamo existente (Pool)",
  inversion: "Inversión CAPEX",
  renting: "Renting",
  poliza: "Producto de circulante",
  evento_balance: "Movimiento de balance",
};

// Pestaña de ProyeccionPage donde se edita cada cosa (ids de tabsConfig).
const pestanaDeDriver = (d) => {
  if (d.seccion === "ingresos") return "pyg-ingresos";
  if (d.seccion === "otros_ingresos") return "pyg-otros";
  if ((d.codigo || "").startsWith("68")) return "capex";
  if ((d.codigo || "").startsWith("66")) return "financiacion";
  return "pyg-gastos";
};

const pestanaDeOperacion = (op) => {
  if (op.tipo === "inversion") return "capex";
  if (op.tipo === "evento_balance") return "otras-hipotesis";
  return "financiacion";
};

const fmtFecha = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }) +
    " " + d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
};

export default function MapaHipotesisContent({ empresaId, onIr }) {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [operaciones, setOperaciones] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportando, setExportando] = useState(false);

  const handleExportar = async () => {
    setExportando(true);
    try {
      const response = await exportarDriversEmpresa(empresaId);
      downloadBlob(response.data, "Drivers.xlsx");
      toast.success("Drivers exportados correctamente");
    } catch (e) {
      toast.error(await parseBlobError(e, "No se pudieron exportar los drivers"));
    } finally {
      setExportando(false);
    }
  };

  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [drvRes, opsRes, cfgRes] = await Promise.all([
        getProyeccionDrivers(empresaId),
        getProyeccionOperaciones(empresaId),
        getProyeccionConfig(empresaId),
      ]);
      setDrivers(drvRes.data?.drivers || []);
      const ops = opsRes.data;
      setOperaciones(Array.isArray(ops) ? ops : ops?.operaciones || []);
      setConfig(cfgRes.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al cargar el mapa de hipótesis");
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>{error}</p>
      </div>
    );
  }

  // Orden estable: sección (ingresos → otros → gastos) y código dentro.
  const ordenSeccion = { ingresos: 0, otros_ingresos: 1, gastos: 2 };
  const driversOrdenados = [...drivers].sort((a, b) => {
    const s = (ordenSeccion[a.seccion] ?? 9) - (ordenSeccion[b.seccion] ?? 9);
    return s !== 0 ? s : String(a.codigo).localeCompare(String(b.codigo));
  });
  const activos = drivers.filter((d) => d.activo).length;

  const irA = (tab) => onIr && onIr(tab);

  // Hipótesis de la DFN (PLAN_DFN.md fase 3): solo se listan si alguien las ha
  // tocado (asimiladas, switch IFCP apagado o umbral distinto del default);
  // con los valores por defecto no hay nada que ver. El «Ir» lleva a la página
  // DFN (`/empresas/:id/dfn`).
  const dfnCfg = config?.dfn || null;
  const dfnAsimiladas = (dfnCfg?.asimiladas || []).filter((a) => a && a.concepto);
  const dfnTocada =
    !!dfnCfg &&
    (dfnAsimiladas.length > 0 || dfnCfg.restar_ifcp === false ||
      (dfnCfg.umbral_conciliacion != null && Number(dfnCfg.umbral_conciliacion) !== 50000));

  return (
    <div className="space-y-6" data-testid="mapa-hipotesis">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-primary/10 text-primary">
            <Map className="h-5 w-5" />
          </div>
          <div>
            <h2 className="section-title">Mapa de hipótesis</h2>
            <p className="text-sm text-muted-foreground">
              Todo lo que hay tocado en esta proyección, en un solo sitio: si alguien
              del equipo aplica un driver o da de alta una operación, aquí se ve — con
              su fecha — sin ir pestaña por pestaña.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 shrink-0"
          onClick={handleExportar}
          disabled={exportando}
          data-testid="exportar-drivers-btn"
        >
          {exportando ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Exportar Excel
        </Button>
      </div>

      {/* ── Drivers por línea ── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <ListChecks className="h-4 w-4 text-primary" />
            Drivers por línea
            <Badge variant="outline" className="text-[11px] font-normal">
              {activos} activos{drivers.length !== activos ? ` · ${drivers.length - activos} sin aplicar` : ""}
            </Badge>
          </CardTitle>
          <CardDescription>
            Las líneas SIN driver proyectan en automático (plano o IPC por defecto).
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {driversOrdenados.length === 0 ? (
            <p className="px-4 pb-4 text-sm text-muted-foreground">
              No hay ningún driver configurado: toda la proyección va en automático.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="text-left py-2 px-4 font-medium">Sección</th>
                  <th className="text-left py-2 px-2 font-medium">Línea</th>
                  <th className="text-left py-2 px-2 font-medium">Hipótesis</th>
                  <th className="text-left py-2 px-2 font-medium">Estado</th>
                  <th className="text-left py-2 px-2 font-medium whitespace-nowrap">Último cambio</th>
                  <th className="py-2 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {driversOrdenados.map((d) => (
                  <tr key={d.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="py-2 px-4 whitespace-nowrap text-muted-foreground">
                      {SECCION_LABEL[d.seccion] || d.seccion}
                    </td>
                    <td className="py-2 px-2">
                      <span className="font-mono text-xs text-muted-foreground mr-1.5">{d.codigo}</span>
                      {d.nombre}
                      <span className="ml-1.5 text-[11px] text-muted-foreground">({NIVEL_LABEL[d.nivel] || d.nivel})</span>
                    </td>
                    <td className="py-2 px-2">{d.descripcion}</td>
                    <td className="py-2 px-2 whitespace-nowrap">
                      {d.activo ? (
                        <Badge variant="outline" className="text-emerald-600 border-emerald-600 text-[11px]">Activo</Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground text-[11px]">No aplica (nivel)</Badge>
                      )}
                    </td>
                    <td className="py-2 px-2 whitespace-nowrap font-mono text-xs text-muted-foreground">
                      {fmtFecha(d.actualizado)}
                    </td>
                    <td className="py-2 px-2 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 gap-1 text-xs"
                        onClick={() => irA(pestanaDeDriver(d))}
                        data-testid={`mapa-ir-${d.id}`}
                      >
                        Ir <ArrowUpRight className="h-3 w-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* ── Operaciones ── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Landmark className="h-4 w-4 text-primary" />
            Operaciones
            <Badge variant="outline" className="text-[11px] font-normal">{operaciones.length}</Badge>
          </CardTitle>
          <CardDescription>
            Préstamos, inversiones CAPEX, rentings, circulante y movimientos manuales de balance.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {operaciones.length === 0 ? (
            <p className="px-4 pb-4 text-sm text-muted-foreground">No hay operaciones dadas de alta.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="text-left py-2 px-4 font-medium">Tipo</th>
                  <th className="text-left py-2 px-2 font-medium">Nombre</th>
                  <th className="py-2 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {operaciones.map((op) => (
                  <tr key={op.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="py-2 px-4 whitespace-nowrap">
                      <Badge variant="secondary" className="text-[11px] font-normal">
                        {TIPO_OPERACION_LABEL[op.tipo] || op.tipo}
                      </Badge>
                    </td>
                    <td className="py-2 px-2">{op.nombre || "—"}</td>
                    <td className="py-2 px-2 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 gap-1 text-xs"
                        onClick={() => irA(pestanaDeOperacion(op))}
                        data-testid={`mapa-ir-op-${op.id}`}
                      >
                        Ir <ArrowUpRight className="h-3 w-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* ── Hipótesis de la DFN ── */}
      {dfnTocada && (
        <Card data-testid="mapa-dfn">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <Landmark className="h-4 w-4 text-primary" />
              Deuda Financiera Neta (DFN)
              <Badge variant="outline" className="text-[11px] font-normal">
                {dfnAsimiladas.length} asimilada{dfnAsimiladas.length === 1 ? "" : "s"}
              </Badge>
            </CardTitle>
            <CardDescription>
              Capa de análisis: no toca el modelo. Otras deudas asimiladas a financiera,
              inversiones financieras c/p y umbral de conciliación con el pool.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
              <div>
                <dt className="text-xs text-muted-foreground">Otras deudas asimiladas</dt>
                <dd className="font-medium">
                  {dfnAsimiladas.length === 0 ? "Ninguna" : dfnAsimiladas.map((a) => a.concepto).join(" · ")}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Inversiones financieras c/p</dt>
                <dd className="font-medium">{dfnCfg.restar_ifcp === false ? "No restan" : "Restan de la DFN"}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Umbral de conciliación</dt>
                <dd className="font-medium flex items-center gap-1.5">
                  {dfnCfg.umbral_conciliacion != null
                    ? `${new Intl.NumberFormat("es-ES").format(Number(dfnCfg.umbral_conciliacion))} €`
                    : "50.000 €"}
                  <button
                    type="button"
                    className="text-primary text-xs inline-flex items-center gap-0.5 hover:underline"
                    onClick={() => navigate(`/empresas/${empresaId}/dfn`)}
                    data-testid="mapa-ir-dfn"
                  >
                    Ir <ArrowUpRight className="h-3 w-3" />
                  </button>
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      )}

      {/* ── Parámetros del modelo ── */}
      {config && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <Settings2 className="h-4 w-4 text-primary" />
                Parámetros del modelo
              </span>
              <button
                type="button"
                className="text-primary text-xs font-normal inline-flex items-center gap-0.5 hover:underline"
                onClick={() => irA("generales")}
                data-testid="mapa-ir-generales"
              >
                Ir a Generales <ArrowUpRight className="h-3 w-3" />
              </button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-3 text-sm">
              <div>
                <dt className="text-xs text-muted-foreground">Horizonte</dt>
                <dd className="font-medium">{config.horizonte_anios} {config.horizonte_anios === 1 ? "año" : "años"}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Año base</dt>
                <dd className="font-medium">{config.anio_base ?? "Último real"}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Impuestos</dt>
                <dd className="font-medium">{config.tasa_impuestos_pct != null ? `${config.tasa_impuestos_pct}%` : "—"}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Payout dividendos</dt>
                <dd className="font-medium">{config.payout_pct != null ? `${config.payout_pct}%` : "—"}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">IPC por defecto</dt>
                <dd className="font-medium">{config.inflacion_pct != null ? `${config.inflacion_pct}%` : "—"}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Nivel de proyección</dt>
                <dd className="font-medium">
                  {config.nivel_proyeccion === "auto" ? "Automático" : NIVEL_LABEL[config.nivel_proyeccion] || config.nivel_proyeccion}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">EBITDA normalizado</dt>
                <dd className="font-medium flex items-center gap-1.5">
                  {(config.ebitda_normalizado?.ajustes || []).length > 0
                    ? `${config.ebitda_normalizado.ajustes.length} ajuste${config.ebitda_normalizado.ajustes.length > 1 ? "s" : ""}`
                    : "Sin ajustes"}
                  <button
                    type="button"
                    className="text-primary text-xs inline-flex items-center gap-0.5 hover:underline"
                    onClick={() => irA("resultados")}
                    data-testid="mapa-ir-ebitda-norm"
                  >
                    Ir <ArrowUpRight className="h-3 w-3" />
                  </button>
                </dd>
              </div>
            </dl>
            <p className="mt-3 text-[11px] text-muted-foreground">
              Año base y horizonte se eligen en la cabecera de Proyección; impuesto, IPC, payout y
              nivel, en Drivers → Generales; las hipótesis de NOF, en Drivers → NOF. El EBITDA
              normalizado se edita en Drivers → Resultados.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
